import * as z from 'zod';
import { MIN_STAGE, MAX_STAGE } from './stages';

/**
 * Zod schemas for the REAL Noor order domain (replacing the placeholder
 * `schema.ts`). Two shapes matter:
 *   - `orderRow` — the broad list-row payload the 17-column table reads.
 *   - `orderDetail` — the rich single-order payload the detail page reads.
 *
 * The backend payload is intentionally BROAD and only partially modelled here:
 * we parse the fields the UI touches and keep the rest via `.loose()` (the
 * cpanel `OrderRow`/`types.Order` both carry index signatures for the same
 * reason). The Go backend marshals its `sql.Null*` wrappers to bare nullable
 * JSON values, so we model bare `…nullish()` values — NOT `{value, valid}`
 * objects — matching what the live cpanel UI actually consumes.
 */

/** Tolerant id — the API sends numeric ids; the UI stringifies for routing. */
export const orderIdSchema = z.union([z.string(), z.number()]);
export type OrderId = z.infer<typeof orderIdSchema>;

/** A geo point. The API uses `{lat, long}`; the map flips to `[long, lat]`. */
export const pointSchema = z.object({
  lat: z.number(),
  long: z.number(),
});
export type GeoPoint = z.infer<typeof pointSchema>;

/** Stage number, clamped to the known 1–29 range but tolerant of unknowns. */
export const stageNumberSchema = z
  .number()
  .int()
  .min(MIN_STAGE)
  .max(MAX_STAGE)
  .catch(MIN_STAGE);

export const paymentTypeSchema = z.enum(['CASH', 'BALANCE', 'CARD']).catch('CARD');
export type PaymentType = z.infer<typeof paymentTypeSchema>;

/** Courier as embedded in a list row / detail header. */
export const orderCourierSchema = z
  .object({
    id: orderIdSchema.nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .loose();
export type OrderCourier = z.infer<typeof orderCourierSchema>;

export const cancelledCourierSchema = z
  .object({
    id: orderIdSchema.nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
  })
  .loose();

/** Vendor (merchant) as embedded in an order. */
export const orderVendorSchema = z
  .object({
    id: orderIdSchema.nullish(),
    name: z.string().nullish(),
    business_name: z.string().nullish(),
    is_business: z.boolean().nullish(),
    is_corporate: z.boolean().nullish(),
  })
  .loose();

/** One route point's timing on a list row (`origin_full` / `destination_full`). */
export const routeLegSchema = z
  .object({
    arrived_at: z.string().nullish(),
    handed_over_at: z.string().nullish(),
    initial_eta: z.string().nullish(),
  })
  .loose();

/**
 * A row of the orders list as delivered by `GET /admin/orders` and consumed by
 * the table cell renderers. Broad + `.loose()`: only the touched fields are
 * named; the real payload is wider.
 */
export const orderRowSchema = z
  .object({
    id: orderIdSchema,
    display_id: orderIdSchema.nullish(),
    stage: stageNumberSchema,
    service_id: z.number().nullish(),
    vendor_order_id: z.string().nullish(),
    payment_type: z.string().nullish(),
    is_business: z.boolean().nullish(),
    is_market: z.boolean().nullish(),
    is_integration: z.boolean().nullish(),
    total_delivery_price: z.number().nullish(),
    total_product_price: z.number().nullish(),
    product_paid: z.boolean().nullish(),
    reason: z.string().nullish(),
    created_at: z.string().nullish(),
    delivery_date: z.string().nullish(),
    search_started_at: z.string().nullish(),
    search_stopped_at: z.string().nullish(),
    handed_over_at: z.string().nullish(),
    courier_paid_at: z.string().nullish(),
    courier_payment_status: z.number().nullish(),
    courier_payment_receipt_url: z.string().nullish(),
    battery_percentage: z.union([z.number(), z.string()]).nullish(),
    /** Metres; numeric strings tolerated at the boundary (cpanel coerces with `Number`). */
    dead_head_distance: z.union([z.number(), z.string()]).nullish(),
    order_distance: z.union([z.number(), z.string()]).nullish(),
    last_updated_by: z.union([z.string(), z.number()]).nullish(),
    last_updated_by_role: z.string().nullish(),
    /** The latest note (cpanel reads `last_note.text`); tolerant of odd shapes. */
    last_note: z.looseObject({ text: z.string().nullish() }).nullish().catch(null),
    origin_address: z.array(z.string()).nullish(),
    destination_address: z.array(z.string()).nullish(),
    courier: orderCourierSchema.nullish(),
    cancelled_couriers: z.array(cancelledCourierSchema).nullish(),
    client: z.object({ id: orderIdSchema.nullish() }).loose().nullish(),
    vendor: orderVendorSchema.nullish(),
    /** Per-point route timing the "Время" timer walks (cpanel `getRouteTimerProps`). */
    origin_full: z.array(routeLegSchema).nullish().catch(null),
    destination_full: z.array(routeLegSchema).nullish().catch(null),
  })
  .loose();
export type OrderRow = z.infer<typeof orderRowSchema>;

/**
 * Tab badge counts — `count_info` from the list envelope. Mirrors openapi
 * `types.MyOrdersCountDto`; every count optional/defaulted so a missing key
 * renders a 0 badge rather than throwing.
 */
export const orderCountInfoSchema = z
  .object({
    total: z.number().nullish(),
    scheduled: z.number().nullish(),
    in_search: z.number().nullish(),
    scooter_count: z.number().nullish(),
    car_count: z.number().nullish(),
    returned: z.number().nullish(),
    not_found: z.number().nullish(),
    active: z.number().nullish(),
    finished: z.number().nullish(),
    cancelled: z.number().nullish(),
    cancelled_with_payment: z.number().nullish(),
    not_paid_count: z.number().nullish(),
    debt_count: z.number().nullish(),
    to_origin: z.number().nullish(),
    wait_order: z.number().nullish(),
    to_destination: z.number().nullish(),
    wait_receiver: z.number().nullish(),
  })
  .loose();
export type OrderCountInfo = z.infer<typeof orderCountInfoSchema>;

/**
 * A vendor option for the orders entity filter (`GET admin/vendors`). Only
 * the label fields cpanel's select reads are named.
 */
export const orderVendorOptionSchema = z
  .object({
    id: orderIdSchema,
    name: z.string().nullish(),
    business_name: z.string().nullish(),
    is_business: z.boolean().nullish(),
    phone: z.string().nullish(),
  })
  .loose();
export type OrderVendorOption = z.infer<typeof orderVendorOptionSchema>;

/** A courier option for the orders courier filter (`GET admin/couriers`). */
export const orderCourierOptionSchema = z
  .object({
    id: orderIdSchema,
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
  })
  .loose();
export type OrderCourierOption = z.infer<typeof orderCourierOptionSchema>;

/**
 * Who last touched an order — the profile the status tooltip resolves by role
 * (`admin/vendors/:id`, `admin/users/:id`, `admin/couriers/:id`). Only the
 * name fields cpanel's `useLastUpdatedBy` reads.
 */
export const orderActorSchema = z
  .object({
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    name: z.string().nullish(),
    business_name: z.string().nullish(),
    is_business: z.boolean().nullish(),
  })
  .loose();
export type OrderActor = z.infer<typeof orderActorSchema>;

/**
 * The raw list envelope from `GET /admin/orders`:
 * `{count, count_info, current_page, last_page, page_size, data}`. The api
 * layer adapts this to the table-friendly `{data, meta, countInfo}` shape.
 */
export const orderListEnvelopeSchema = z
  .object({
    data: z.array(orderRowSchema).nullish(),
    count: z.number().nullish(),
    count_info: orderCountInfoSchema.nullish(),
    current_page: z.number().nullish(),
    last_page: z.number().nullish(),
    page_size: z.number().nullish(),
  })
  .loose();
export type OrderListEnvelope = z.infer<typeof orderListEnvelopeSchema>;

/**
 * A single product line item inside a route point's `products.items`. Broad +
 * `.loose()`; the detail page's product tables read name/weight/dimensions/
 * quantity/price_per_unit. Weight is grams, dimensions cm (see lib/products).
 */
export const orderProductItemSchema = z
  .object({
    name: z.string().nullish(),
    image: z.string().nullish(),
    image_url: z.string().nullish(),
    image_path: z.string().nullish(),
    photo: z.string().nullish(),
    photo_url: z.string().nullish(),
    weight: z.number().nullish(),
    height: z.number().nullish(),
    width: z.number().nullish(),
    length: z.number().nullish(),
    quantity: z.number().nullish(),
    price_per_unit: z.number().nullish(),
  })
  .loose();
export type OrderProductItem = z.infer<typeof orderProductItemSchema>;

/** A full route point (origin/destination) on the order detail. */
export const orderPointSchema = z
  .object({
    id: z.string().nullish(),
    type: z.enum(['origin', 'destination']).nullish(),
    order: z.number().nullish(),
    address: z.string().nullish(),
    location: pointSchema.nullish(),
    entrance: z.string().nullish(),
    door_phone: z.string().nullish(),
    floor: z.union([z.number(), z.string()]).nullish(),
    apartment: z.string().nullish(),
    comment: z.string().nullish(),
    arrived_at: z.string().nullish(),
    handed_over_at: z.string().nullish(),
    initial_eta: z.string().nullish(),
    eta: z.string().nullish(),
    closed_point_at_distance: z.number().nullish(),
    client: z
      .object({
        id: orderIdSchema.nullish(),
        name: z.string().nullish(),
        phone: z.string().nullish(),
        email: z.string().nullish(),
      })
      .loose()
      .nullish(),
    products: z
      .object({
        type_id: z.number().nullish(),
        description: z.string().nullish(),
        items: z.array(orderProductItemSchema).nullish(),
      })
      .loose()
      .nullish(),
  })
  .loose();
export type OrderPoint = z.infer<typeof orderPointSchema>;

/**
 * The `pricing` bag on a detail order. The price-formula card reads these nine
 * fields (all sums except `discount` which is a %). Bare nullable values +
 * `.loose()` to tolerate the wider backend payload.
 */
export const orderPricingSchema = z
  .object({
    products: z.number().nullish(),
    delivery_base_price: z.number().nullish(),
    base_price_paid: z.boolean().nullish(),
    courier_profit: z.number().nullish(),
    total: z.number().nullish(),
    courier_profit_add_on: z.number().nullish(),
    discount: z.number().nullish(),
    wait_price: z.number().nullish(),
  })
  .loose();
export type OrderPricing = z.infer<typeof orderPricingSchema>;

/** Equipment (transport type) names, localized by the backend. */
export const orderEquipmentSchema = z
  .object({
    name_ru: z.string().nullish(),
    name_uz: z.string().nullish(),
    name_en: z.string().nullish(),
  })
  .loose();
export type OrderEquipment = z.infer<typeof orderEquipmentSchema>;

/** The `delivery` bag: scheduling + flags read by the info card. */
export const orderDeliverySchema = z
  .object({
    type: z.string().nullish(),
    door_to_door: z.boolean().nullish(),
    product_paid: z.boolean().nullish(),
    time: z.string().nullish(),
    equipment: orderEquipmentSchema.nullish(),
  })
  .loose();
export type OrderDelivery = z.infer<typeof orderDeliverySchema>;

/** Pricing tariff (`{name}`) shown in the price-formula card. */
export const orderTariffSchema = z.object({ name: z.string().nullish() }).loose();

/**
 * One change-log entry (`logs[]`). `changed_values` is a JSON STRING the UI
 * parses on demand (the change-diff dialog + stage-history derivation). Broad +
 * `.loose()`.
 */
export const orderLogSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    table_name: z.string().nullish(),
    operation: z.string().nullish(),
    changed_values: z.string().nullish(),
    order_id: orderIdSchema.nullish(),
    created_at: z.string().nullish(),
    fullname: z.string().nullish(),
    role: z.string().nullish(),
  })
  .loose();
export type OrderLog = z.infer<typeof orderLogSchema>;

/**
 * One payment transaction (`transaction[]`). The transactions table reads
 * amount/type/status/reason + the entity ids that pick a badge label. Status
 * and type are numeric codes (see lib/transactions).
 */
export const orderTransactionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    display_id: z.union([z.string(), z.number()]).nullish(),
    created_at: z.string().nullish(),
    status: z.number().nullish(),
    type: z.number().nullish(),
    reason: z.string().nullish(),
    amount: z.number().nullish(),
    bonus_id: z.union([z.string(), z.number()]).nullish(),
    bonus_order_count: z.number().nullish(),
    courier_id: orderIdSchema.nullish(),
    vendor_id: orderIdSchema.nullish(),
    client_id: orderIdSchema.nullish(),
    phone: z.string().nullish(),
    explanation: z.looseObject({ description: z.string().nullish() }).nullish(),
  })
  .loose();
export type OrderTransaction = z.infer<typeof orderTransactionSchema>;

/**
 * The single-order detail payload (`GET /orders/{id}` → `types.Order`). Broad +
 * `.loose()` — the detail page reads many nested sections; we name the ones the
 * UI touches and tolerate the rest.
 */
export const orderDetailSchema = z
  .object({
    id: orderIdSchema,
    display_id: orderIdSchema.nullish(),
    stage: stageNumberSchema,
    vendor_order_id: z.string().nullish(),
    service_id: z.number().nullish(),
    payment_type: z.string().nullish(),
    parent_order_id: z.union([z.string(), z.number()]).nullish(),
    is_business: z.boolean().nullish(),
    is_market: z.boolean().nullish(),
    is_integration: z.boolean().nullish(),
    is_promotion: z.boolean().nullish(),
    reason: z.string().nullish(),
    link: z.string().nullish(),
    payme_link: z.string().nullish(),
    token: z.string().nullish(),
    created_at: z.string().nullish(),
    accepted_at: z.string().nullish(),
    search_started_at: z.string().nullish(),
    search_stopped_at: z.string().nullish(),
    courier_paid_at: z.string().nullish(),
    courier_payment_status: z.number().nullish(),
    courier_payment_receipt_url: z.string().nullish(),
    last_updated_by: z.union([z.string(), z.number()]).nullish(),
    total_late_minutes: z.number().nullish(),
    free_wait_time: z.number().nullish(),
    /** Distances in metres; returned by the migrated order detail endpoint. */
    dead_head_distance: z.number().nullish(),
    order_distance: z.number().nullish(),
    origin: z.array(orderPointSchema).nullish(),
    destination: z.array(orderPointSchema).nullish(),
    courier: orderCourierSchema.nullish(),
    vendor: orderVendorSchema.nullish(),
    delivery: orderDeliverySchema.nullish(),
    pricing: orderPricingSchema.nullish(),
    tariff: orderTariffSchema.nullish(),
    equipment: orderEquipmentSchema.nullish(),
    distances: z.looseObject({}).nullish(),
    card: z.looseObject({}).nullish(),
    rating: z.looseObject({}).nullish(),
    return_address: z.looseObject({}).nullish(),
    trace: z.array(z.unknown()).nullish(),
    logs: z.array(orderLogSchema).nullish(),
    transaction: z.array(orderTransactionSchema).nullish(),
  })
  .loose();
export type OrderDetail = z.infer<typeof orderDetailSchema>;

/**
 * One courier offer during assignment (`GET /admin/orders/{id}/offers`). The
 * assignment tab reads the offer status (its lifecycle), the courier location
 * (radial map), and the response timing. `status` is a stable slug.
 */
export const offerStatusSchema = z
  .enum(['offered', 'viewed', 'responded', 'rejected', 'accepted'])
  .catch('offered');
export type OfferStatus = z.infer<typeof offerStatusSchema>;

export const orderOfferSchema = z
  .object({
    courier_id: orderIdSchema.nullish(),
    courier_name: z.string().nullish(),
    status: offerStatusSchema,
    location: pointSchema.nullish(),
    sent_at: z.string().nullish(),
    responded_at: z.string().nullish(),
    /** Seconds the courier took to act (offered → responded/accepted). */
    response_seconds: z.number().nullish(),
  })
  .loose();
export type OrderOffer = z.infer<typeof orderOfferSchema>;

/** Assignment summary for an order: offer counts + timing health. */
export const orderAssignmentSchema = z
  .object({
    offers: z.array(orderOfferSchema).nullish(),
    offers_sent: z.number().nullish(),
    viewed: z.number().nullish(),
    responded: z.number().nullish(),
    rejected: z.number().nullish(),
    accepted: z.number().nullish(),
    /** Seconds from search start to acceptance. */
    assignment_seconds: z.number().nullish(),
  })
  .loose();
export type OrderAssignment = z.infer<typeof orderAssignmentSchema>;

/** Live courier location (`GET /orders/{id}/couriers/{cid}/location`). */
export const courierLocationSchema = z
  .object({
    location: pointSchema.nullish(),
    last_reported_at: z.string().nullish(),
  })
  .loose();
export type CourierLocation = z.infer<typeof courierLocationSchema>;

/** Battery + location (`…/battery-location`). */
export const courierBatteryLocationSchema = z
  .object({
    location: pointSchema.nullish(),
    last_reported_at: z.string().nullish(),
    battery_percentage: z.union([z.number(), z.string()]).nullish(),
  })
  .loose();
export type CourierBatteryLocation = z.infer<typeof courierBatteryLocationSchema>;

/**
 * One courier offer as the offers VISUALIZATION reads it — a row of the
 * Delivery service's `GET /api/v1/admin/orders/{id}/offers` `list`. (The
 * Assignment tab reads a different, leaner shape of the main gateway's route
 * through {@link orderOfferSchema}.)
 */
export const orderMapOfferSchema = z
  .object({
    id: orderIdSchema.nullish(),
    order_id: orderIdSchema.nullish(),
    courier_id: orderIdSchema.nullish(),
    courier_location: z
      .object({
        lat: z.number().nullish().catch(null),
        long: z.number().nullish().catch(null),
      })
      .nullish()
      .catch(null),
    /** Road distance courier → pickup, metres. */
    dead_head_distance: z.number().nullish().catch(null),
    /** Why the courier was not offered / did not take it (see `OFFER_REASONS`). */
    reason: z.number().nullish().catch(null),
    /** Outcome slug, e.g. `reject_reason_2`. */
    offer_decision: z.string().nullish().catch(null),
    /** Base64 JSON with the dispatch stage that decided, e.g. `{"stage":"basic_check"}`. */
    decision_detail: z.string().nullish().catch(null),
    /** Times the offer reached the courier's screen. */
    offer_count: z.number().nullish().catch(null),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
  })
  .loose();
export type OrderMapOffer = z.infer<typeof orderMapOfferSchema>;

/**
 * The offers `list`. A malformed row is dropped on its own rather than
 * failing the whole list, so one bad offer never blanks the map.
 */
export const orderOffersSchema = z
  .array(z.unknown())
  .nullish()
  .transform((list) =>
    (list ?? []).flatMap((item) => {
      const parsed = orderMapOfferSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    }),
  );
export type OrderOffers = z.infer<typeof orderOffersSchema>;

/**
 * One courier offer as cpanel's `LogsInfo` reads it — a row of the main
 * gateway's `GET /admin/orders/{id}/offers` (`offers`). Unlike the Delivery row
 * it carries the courier's name and id, and the moments it reached the
 * courier's screen (`offered_at`) and was taken (`accepted_at`); `reason`
 * numbers follow cpanel's own table, not the backend enum. Real rows also carry
 * `order_display_id`, `equipment_id/_photo`, `vendor_id/_name` and `created_at`
 * — unused here, kept by `.loose()`. Both timestamps are `null` until the offer
 * reaches the courier, so a rejected-up-front offer has neither.
 */
export const legacyOfferSchema = z
  .object({
    id: orderIdSchema.nullish().catch(null),
    /** The courier's display id (`#205`), as cpanel prints it. */
    courier_courier_id: orderIdSchema.nullish().catch(null),
    courier_first_name: z.string().nullish().catch(null),
    courier_last_name: z.string().nullish().catch(null),
    courier_location: z
      .object({
        lat: z.number().nullish().catch(null),
        long: z.number().nullish().catch(null),
      })
      .nullish()
      .catch(null),
    /** Road distance courier → pickup, metres. */
    dead_head_distance: z.number().nullish().catch(null),
    reason: z.number().nullish().catch(null),
    offered_at: z.string().nullish().catch(null),
    accepted_at: z.string().nullish().catch(null),
    equipment_name: z.string().nullish().catch(null),
  })
  .loose();
export type LegacyOffer = z.infer<typeof legacyOfferSchema>;

/**
 * Body of the legacy offers response (`{ offers, statistics }`, unwrapped —
 * there is no `data` envelope): the offers plus the server-side `statistics`
 * tally (`{ "<reason>": count }`, `"0"` = no reason) the legend is drawn from.
 * A malformed offer or count is dropped on its own, never the whole picture.
 */
export const legacyOffersSchema = z
  .object({
    offers: z
      .array(z.unknown())
      .nullish()
      .transform((list) =>
        (list ?? []).flatMap((item) => {
          const parsed = legacyOfferSchema.safeParse(item);
          return parsed.success ? [parsed.data] : [];
        }),
      ),
    statistics: z
      .record(z.string(), z.unknown())
      .nullish()
      .catch(null)
      .transform((counts) =>
        Object.fromEntries(
          Object.entries(counts ?? {}).filter(
            (entry): entry is [string, number] =>
              typeof entry[1] === 'number' && Number.isFinite(entry[1]),
          ),
        ),
      ),
  })
  .loose()
  .nullish()
  .transform((payload) => payload ?? { offers: [], statistics: {} });
export type LegacyOffersPayload = z.infer<typeof legacyOffersSchema>;
